import { getIn, useFormikContext } from "formik";
import { useCallback, useEffect, useState, useRef } from "react";
import { pluck } from "../../helpers/pluck";

type SelectFormikProps = {
  name: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement> | { target: { value: string } }) => void;
  className?: string;
  label?: string;
  required?: boolean;
  options: Array<string>;
  defaultValue?: string;
  placeholder?: string;
  allowFilter?: boolean;
};

const SelectFormik: React.FC<SelectFormikProps> = ({
  name,
  onChange,
  className,
  label,
  required,
  options,
  defaultValue = "",
  placeholder = "Digite para filtrar...",
  allowFilter = false
}) => {
  const { values, errors, setFieldValue, touched, setFieldTouched } = useFormikContext();
  const data = getIn(values, name);

  const [value, setValue] = useState(data || defaultValue);
  const [inputValue, setInputValue] = useState(data || defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filtrar opções baseado no input
  useEffect(() => {
    if (allowFilter && inputValue) {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [inputValue, options, allowFilter]);

  // Atualizar Formik quando valor muda
  useEffect(() => {
    setFieldValue(name, value);
    // Marcar campo como tocado para ativar validação
    if (value !== data) {
      setFieldTouched(name, true);
    }
  }, [name, setFieldValue, setFieldTouched, value, data]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Marcar como tocado quando perder o foco
        setFieldTouched(name, true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [name, setFieldTouched]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Se o valor está vazio, limpar seleção
    if (newValue === "") {
      setValue("");
      if (onChange) {
        onChange({ target: { value: "" } });
      }
    }
    // Se o valor digitado é exato, selecionar automaticamente
    else if (options.includes(newValue)) {
      setValue(newValue);
      if (onChange) {
        onChange({ target: { value: newValue } });
      }
    }
    // Se não encontrou opção exata, limpar valor mas manter inputValue para filtro
    else {
      setValue("");
      if (onChange) {
        onChange({ target: { value: "" } });
      }
    }

    setIsOpen(true);
  }, [options, onChange]);

  const handleSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setInputValue(newValue);
    setFieldTouched(name, true);
    if (onChange) {
      onChange(e);
    }
  }, [onChange, name, setFieldTouched]);

  const handleOptionClick = useCallback((option: string) => {
    setValue(option);
    setInputValue(option);
    setIsOpen(false);
    setFieldTouched(name, true);

    if (onChange) {
      onChange({ target: { value: option } });
    }
  }, [onChange, name, setFieldTouched]);

  const handleInputFocus = () => {
    if (allowFilter) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = () => {
    setFieldTouched(name, true);

    // Se o input não corresponde a uma opção válida, limpar
    if (allowFilter && inputValue && !options.includes(inputValue)) {
      setInputValue("");
      setValue("");
      if (onChange) {
        onChange({ target: { value: "" } });
      }
    }
  };

  const theError = pluck(name, errors) || undefined;
  const isTouched = getIn(touched, name);
  const showError = theError && isTouched;

  // Se allowFilter está desabilitado, usar select tradicional
  if (!allowFilter) {
    return (
      <div className={`h-20 ${className}`}>
        <label htmlFor={name} className="block text-sm font-medium text-gray-600">
          {label} {required ? <b className="text-redPrimary">*</b> : ''}
        </label>
        <select
          onChange={handleSelectChange}
          onBlur={() => setFieldTouched(name, true)}
          value={value}
          className={`border ${showError ? 'border-red-500' : 'border-gray'} h-[43px] text-gray2 p-2 rounded w-full bg-[#ffffff]`}
          aria-required={required}
          aria-invalid={Boolean(showError)}
          id={name}
        >
          <option value="" disabled>Selecione...</option>
          {options.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
        {showError ? <small className="text-redPrimary/90" aria-live="polite">{theError}</small> : null}
      </div>
    );
  }

  // Versão com filtro
  return (
    <div className={`relative h-20 ${className}`} ref={dropdownRef}>
      <label htmlFor={name} className="block text-sm font-medium text-gray-600">
        {label} {required ? <b className="text-redPrimary">*</b> : ''}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className={`border ${showError ? 'border-red-500' : 'border-gray'} h-[43px] text-gray2 p-2 pr-8 rounded w-full bg-[#ffffff]`}
          aria-required={required}
          aria-invalid={Boolean(showError)}
          id={name}
          autoComplete="off"
        />

        {/* Ícone dropdown */}
        <div
          className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Dropdown com opções filtradas */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-700"
                  onClick={() => handleOptionClick(option)}
                >
                  {option}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">
                Nenhuma opção encontrada
              </div>
            )}
          </div>
        )}
      </div>

      {showError ? <small className="text-redPrimary/90" aria-live="polite">{theError}</small> : null}
    </div>
  );
};

export default SelectFormik;